import gql from 'graphql-tag';

import { AlbumRootAlbumFolderPurchaseDetails } from './album-folder';
import {
  AlbumWatermarkConfig,
  PhotoUrlTypeFields,
} from '../../../../graphql/fragments/album';
import {
  WebGalleryAlbumAccessFields,
  WebGalleryAlbumBaseDetails,
  WebGalleryAlbumCustomPrintFields,
  WebGalleryAlbumPrices,
  WebGalleryAlbumPurchasedPhotoDownloads,
} from '../fragments/album';
import { WebGalleryAccountDetailsWithImageAssets } from '../fragments/account';
import { BlogPostListingDetails } from '../fragments/blog';
import { VideoDetails } from '../fragments/video';
import { PhotoAttribution } from '../fragments/global';
import { AutoContributorSettingsDetails } from '../../../../graphql/fragments/contributor-assignments';
import {
  PhotoFavoriteDetails,
  PhotoForegroundDetails,
  PhotoWallpaperWatermarkDetails,
} from '../../../../graphql/fragments/photo';

export const AlbumForWebGalleries = gql`
  query albumForWebGalleries($id: RecordID!) {
    album(id: $id) {
      ...WebGalleryAlbumBaseDetails
      ...WebGalleryAlbumPrices
      ...AlbumWatermarkConfig
      ...WebGalleryAlbumPurchasedPhotoDownloads
      album_folder {
        id
        name
        individual_albums
      }
      cover_photo {
        id
        faces_box
        thumbnail_urls {
          ...PhotoUrlTypeFields
        }
      }
      root_album_folder {
        id
        name
        join_code
        account_has_onboarded
        individual_albums
        price {
          value
        }
        album_type
        joins_enabled
        hero_image {
          id
          url
        }
        web_gallery_price_per_photo {
          value
          currency
        }
        auto_contributor_share_url
        auto_contributor_settings {
          ...AutoContributorSettingsDetails
        }
      }
      creator {
        ...WebGalleryAccountDetailsWithImageAssets
      }
      ...WebGalleryAlbumCustomPrintFields
      ...WebGalleryAlbumAccessFields
      auto_contributor_share_url
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      purchased_event_addon_types
      videos {
        total
      }
    }
  }
  ${WebGalleryAlbumBaseDetails}
  ${AlbumWatermarkConfig}
  ${WebGalleryAccountDetailsWithImageAssets}
  ${WebGalleryAlbumPrices}
  ${WebGalleryAlbumPurchasedPhotoDownloads}
  ${WebGalleryAlbumCustomPrintFields}
  ${WebGalleryAlbumAccessFields}
  ${AutoContributorSettingsDetails}
`;

export const AlbumForWebGalleriesPurchasingOptions = gql`
  query AlbumForWebGalleriesPurchasingOptions(
    $id: RecordID!
    $album_folder_id: RecordID
  ) {
    album(id: $id) {
      ...WebGalleryAlbumBaseDetails
      ...WebGalleryAlbumPrices
      ...AlbumWatermarkConfig
      ...WebGalleryAlbumPurchasedPhotoDownloads
      album_folder {
        id
        name
        individual_albums
      }
      cover_photo {
        id
        faces_box
        thumbnail_urls {
          ...PhotoUrlTypeFields
        }
      }
      ...AlbumRootAlbumFolderPurchaseDetails
      creator {
        ...WebGalleryAccountDetailsWithImageAssets
      }
      ...WebGalleryAlbumCustomPrintFields
      ...WebGalleryAlbumAccessFields
      auto_contributor_share_url
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      purchased_event_addon_types
      videos {
        total
      }
    }
  }
  ${WebGalleryAlbumBaseDetails}
  ${AlbumWatermarkConfig}
  ${WebGalleryAccountDetailsWithImageAssets}
  ${WebGalleryAlbumPrices}
  ${WebGalleryAlbumPurchasedPhotoDownloads}
  ${WebGalleryAlbumCustomPrintFields}
  ${WebGalleryAlbumAccessFields}
  ${AutoContributorSettingsDetails}
  ${AlbumRootAlbumFolderPurchaseDetails}
`;

export const AlbumForWebGalleriesPhotos = gql`
  query AlbumForWebGalleriesPhotos(
    $id: RecordID!
    $ignore_watermark: Boolean = true
    $limit: Int
    $offset: Int
    $for_web_gallery: Boolean
    $use_all_photos_mode: Boolean = false
  ) {
    album(id: $id) {
      id
      ...WebGalleryAlbumCustomPrintFields
      photos_ungrouped: photos(
        include_unprocessed: false
        ungrouped_only: true
        slice: { limit: $limit, offset: $offset }
        for_web_gallery: $for_web_gallery
        use_all_photos_mode: $use_all_photos_mode
      ) {
        total
        records {
          id
          price_album_folder_tag(album_id: $id) {
            ... on IRecord {
              id
            }
            ... on IAlbumFolderTag {
              name
              type
              is_pricing_tag
              digital_downloads_enabled
              digital_downloads_price {
                value
                currency
              }
            }
          }
          thumbnail_urls(ignore_watermark: $ignore_watermark) {
            ...PhotoUrlTypeFields
          }
          watermarked_urls {
            ...PhotoUrlTypeFields
          }
          ...PhotoWallpaperWatermarkDetails
          ...PhotoAttribution
          share_blocked
          is_entitled_matched_photo
          is_owner
          ...PhotoFavoriteDetails
        }
      }
      photos(
        for_web_gallery: $for_web_gallery
        use_all_photos_mode: $use_all_photos_mode
      ) {
        total
      }
    }
  }
  ${PhotoUrlTypeFields}
  ${PhotoAttribution}
  ${PhotoFavoriteDetails}
  ${PhotoWallpaperWatermarkDetails}
  ${WebGalleryAlbumCustomPrintFields}
`;

export const AlbumForWebGalleriesAllPhotos = gql`
  query AlbumForWebGalleriesPhotos(
    $id: RecordID!
    $ignore_watermark: Boolean = true
    $limit: Int
    $offset: Int
    $for_web_gallery: Boolean
    $use_all_photos_mode: Boolean = false
  ) {
    album(id: $id) {
      id
      ...WebGalleryAlbumCustomPrintFields
      photos(
        include_unprocessed: false
        slice: { limit: $limit, offset: $offset }
        for_web_gallery: $for_web_gallery
        use_all_photos_mode: $use_all_photos_mode
      ) {
        total
        records {
          id
          price_album_folder_tag(album_id: $id) {
            ... on IRecord {
              id
            }
            ... on IAlbumFolderTag {
              name
              type
              is_pricing_tag
              digital_downloads_enabled
              digital_downloads_price {
                value
                currency
              }
            }
          }
          thumbnail_urls(ignore_watermark: $ignore_watermark) {
            ...PhotoUrlTypeFields
          }
          watermarked_urls {
            ...PhotoUrlTypeFields
          }
          ...PhotoWallpaperWatermarkDetails
          ...PhotoAttribution
          share_blocked
          is_entitled_matched_photo
          is_owner
        }
      }
    }
  }
  ${PhotoUrlTypeFields}
  ${PhotoAttribution}
  ${PhotoWallpaperWatermarkDetails}
  ${WebGalleryAlbumCustomPrintFields}
`;

export const AlbumPhotoGroupsForWebGallery = gql`
  query albumPhotoGroupsForWebGallery(
    $id: RecordID!
    $ignore_watermark: Boolean = true
    $limit: Int = 10
    $offset: Int = 0
  ) {
    album(id: $id) {
      id
      album_photo_groups(
        slice: { offset: $offset, limit: $limit }
        with_published_photos_only: true
      ) {
        total
        records {
          id
          name
          cover_photos(for_web_gallery: true) {
            records {
              id
              faces_box
              thumbnail_urls(ignore_watermark: $ignore_watermark) {
                ...PhotoUrlTypeFields
              }
              ...PhotoForegroundDetails
            }
          }
        }
      }
    }
  }
  ${PhotoUrlTypeFields}
  ${PhotoForegroundDetails}
`;

export const AlbumWithBlogPosts = gql`
  query albumWithBlogPosts($id: RecordID!, $limit: Int = 20, $offset: Int = 0) {
    album(id: $id) {
      id
      name
      linked_blog_posts(slice: { limit: $limit, offset: $offset }) {
        total
        count
        records {
          ...BlogPostListingDetails
        }
      }
    }
  }
  ${BlogPostListingDetails}
`;

export const AlbumPhotoGroupWithBlogPosts = gql`
  query albumPhotoGroupWithBlogPosts(
    $id: RecordID!
    $limit: Int = 20
    $offset: Int = 0
  ) {
    albumPhotoGroup(id: $id) {
      id
      name
      album {
        id
      }
      linked_blog_posts(slice: { limit: $limit, offset: $offset }) {
        total
        count
        records {
          ...BlogPostListingDetails
        }
      }
    }
  }
  ${BlogPostListingDetails}
`;

export const AlbumWithVideos = gql`
  query albumWithVideos($id: RecordID!, $limit: Int = 25, $offset: Int = 0) {
    album(id: $id) {
      id
      name
      is_video_album
      creator {
        id
      }
      videos(slice: { limit: $limit, offset: $offset }, processed_only: false) {
        total
        records {
          ...VideoDetails
        }
      }
    }
  }
  ${VideoDetails}
`;

export const AlbumPhotoGroupWithVideos = gql`
  query albumPhotoGroupWithVideos(
    $id: RecordID!
    $limit: Int = 20
    $offset: Int = 0
  ) {
    albumPhotoGroup(id: $id) {
      id
      name
      album {
        id
        creator {
          id
        }
      }
      videos(slice: { limit: $limit, offset: $offset }) {
        total
        records {
          ...VideoDetails
        }
      }
    }
  }
  ${VideoDetails}
`;
